const MAX_EVENTS = 100
const MAX_AGE_MS = 24 * 60 * 60 * 1000
const RETRY_DELAYS_MS = [30_000, 120_000, 900_000]
const HIGH_VALUE_EVENTS = new Set(['booking_confirmed', 'booking_intent_created', 'booking_details_submitted', 'payment_handoff_started', 'room_selected', 'rate_search_submitted', 'site_handoff_started', 'site_handoff_received', 'form_succeeded', 'form_submitted', 'contact_selected'])
const timers = new Map()

const readQueue = (storage, key, now) => {
  try {
    const value = JSON.parse(storage.getItem(key) || '[]')
    return Array.isArray(value) ? value.filter((item) => item?.event?.event_id && now - item.queued_at < MAX_AGE_MS) : []
  } catch { return [] }
}
const writeQueue = (storage, key, queue) => {
  try { queue.length ? storage.setItem(key, JSON.stringify(queue)) : storage.removeItem(key) } catch {}
}
const trimQueue = (queue) => {
  while (queue.length > MAX_EVENTS) {
    const low = queue.findIndex((item) => !HIGH_VALUE_EVENTS.has(item.event.event_name))
    queue.splice(low >= 0 ? low : 0, 1)
  }
  return queue
}
const nextDelay = (attempts) => RETRY_DELAYS_MS[Math.min(attempts, RETRY_DELAYS_MS.length) - 1]
async function postEvents(endpoint, events, fetchImpl) {
  const response = await fetchImpl(endpoint, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ events }), keepalive: true, credentials: 'same-origin' })
  let result = {}
  try { result = await response.json() } catch {}
  if (response.ok && result.forwarded === true) return 'delivered'
  if (response.ok && result.retryable !== true) return 'discarded'
  return 'retry'
}
export function clearAnalyticsDeliveryQueue(storage, key) {
  try { storage.removeItem(key) } catch {}
  if (timers.has(key)) clearTimeout(timers.get(key))
  timers.delete(key)
}
export async function flushAnalyticsDeliveryQueue({ endpoint, storage, storageKey, hasConsent, fetchImpl = fetch, now = Date.now() }) {
  if (!hasConsent()) return clearAnalyticsDeliveryQueue(storage, storageKey)
  const queue = readQueue(storage, storageKey, now)
  const due = queue.filter((item) => item.next_attempt_at <= now).slice(0, 20)
  if (!due.length) return writeQueue(storage, storageKey, queue)
  let outcome = 'retry'
  try { outcome = await postEvents(endpoint, due.map((item) => item.event), fetchImpl) } catch {}
  const ids = new Set(due.map((item) => item.event.event_id))
  const remaining = queue.filter((item) => !ids.has(item.event.event_id))
  if (outcome === 'retry') due.forEach((item) => { const attempts = item.attempts + 1; remaining.push({ ...item, attempts, next_attempt_at: now + nextDelay(attempts) }) })
  writeQueue(storage, storageKey, trimQueue(remaining))
  if (outcome === 'retry' && remaining.length && !timers.has(storageKey)) {
    const wait = Math.max(1000, Math.min(...remaining.map((item) => item.next_attempt_at)) - Date.now())
    timers.set(storageKey, setTimeout(async () => {
      timers.delete(storageKey)
      await flushAnalyticsDeliveryQueue({ endpoint, storage, storageKey, hasConsent, fetchImpl })
    }, wait))
  }
}
export async function deliverAnalyticsEvent({ endpoint, event, storage, storageKey, hasConsent, fetchImpl = fetch, now = Date.now() }) {
  if (!hasConsent()) return
  let outcome = 'retry'
  try { outcome = await postEvents(endpoint, [event], fetchImpl) } catch {}
  if (outcome !== 'retry' || !hasConsent()) return
  const queue = readQueue(storage, storageKey, now)
  if (!queue.some((item) => item.event.event_id === event.event_id)) queue.push({ event, queued_at: now, attempts: 1, next_attempt_at: now + RETRY_DELAYS_MS[0] })
  writeQueue(storage, storageKey, trimQueue(queue))
  if (!timers.has(storageKey)) timers.set(storageKey, setTimeout(async () => { timers.delete(storageKey); await flushAnalyticsDeliveryQueue({ endpoint, storage, storageKey, hasConsent, fetchImpl }) }, RETRY_DELAYS_MS[0]))
}
